import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useClient } from 'urql';

import { Dimension1, SENTIMENT_GET_MAP_DATA } from '@revelio/data-access';

import type { Competitor } from './useCompetitors';
import type { ResolvedProfileIds } from './useResolveProfileIds';

/**
 * All 46 sentiment dimensions available from the API.
 * Mirrors the full list used on the Sentiment Topics page.
 */
export interface SentimentScores {
  overall: number | null;
  business_outlook: number | null;
  career_growth: number | null;
  compensation: number | null;
  benefits: number | null;
  healthcare: number | null;
  retirement_pension: number | null;
  childcare: number | null;
  education_assistance: number | null;
  wellness_programs: number | null;
  work_life_balance: number | null;
  diversity_and_inclusion: number | null;
  leadership: number | null;
  culture: number | null;
  remote_work: number | null;
  onboarding: number | null;
  mentorship: number | null;
  technology: number | null;
  promotion_opportunities: number | null;
  paid_time_off: number | null;
  transparency: number | null;
  efficiency_and_bureaucracy: number | null;
  product_and_service_quality: number | null;
  prestige: number | null;
  coworkers: number | null;
  mission_and_vision: number | null;
  workplace_facilities: number | null;
  training: number | null;
  clients_and_customers: number | null;
  company_growth: number | null;
  schedule_flexibility: number | null;
  job_security: number | null;
  overtime: number | null;
  family_support: number | null;
  commission: number | null;
  independence: number | null;
  commute: number | null;
  safety: number | null;
  internal_communication: number | null;
  office_politics: number | null;
  harassment: number | null;
  honesty: number | null;
  ethics: number | null;
  commitment: number | null;
  compliance: number | null;
  integrity: number | null;
}

export const SENTIMENT_KEYS: Array<keyof SentimentScores> = [
  'overall',
  'business_outlook',
  'career_growth',
  'compensation',
  'benefits',
  'healthcare',
  'retirement_pension',
  'childcare',
  'education_assistance',
  'wellness_programs',
  'work_life_balance',
  'diversity_and_inclusion',
  'leadership',
  'culture',
  'remote_work',
  'onboarding',
  'mentorship',
  'technology',
  'promotion_opportunities',
  'paid_time_off',
  'transparency',
  'efficiency_and_bureaucracy',
  'product_and_service_quality',
  'prestige',
  'coworkers',
  'mission_and_vision',
  'workplace_facilities',
  'training',
  'clients_and_customers',
  'company_growth',
  'schedule_flexibility',
  'job_security',
  'overtime',
  'family_support',
  'commission',
  'independence',
  'commute',
  'safety',
  'internal_communication',
  'office_politics',
  'harassment',
  'honesty',
  'ethics',
  'commitment',
  'compliance',
  'integrity',
];

export const SENTIMENT_LABELS: Record<keyof SentimentScores, string> = {
  overall: 'Overall',
  business_outlook: 'Business Outlook',
  career_growth: 'Career Growth',
  compensation: 'Compensation',
  benefits: 'Benefits',
  healthcare: 'Healthcare',
  retirement_pension: 'Retirement & Pension',
  childcare: 'Childcare',
  education_assistance: 'Education Assistance',
  wellness_programs: 'Wellness Programs',
  work_life_balance: 'Work-Life Balance',
  diversity_and_inclusion: 'Diversity & Inclusion',
  leadership: 'Leadership',
  culture: 'Culture',
  remote_work: 'Remote Work',
  onboarding: 'Onboarding',
  mentorship: 'Mentorship',
  technology: 'Technology',
  promotion_opportunities: 'Promotion Opportunities',
  paid_time_off: 'Paid Time Off',
  transparency: 'Transparency',
  efficiency_and_bureaucracy: 'Efficiency & Bureaucracy',
  product_and_service_quality: 'Product & Service Quality',
  prestige: 'Prestige',
  coworkers: 'Coworkers',
  mission_and_vision: 'Mission & Vision',
  workplace_facilities: 'Workplace Facilities',
  training: 'Training',
  clients_and_customers: 'Clients & Customers',
  company_growth: 'Company Growth',
  schedule_flexibility: 'Schedule Flexibility',
  job_security: 'Job Security',
  overtime: 'Overtime',
  family_support: 'Family Support',
  commission: 'Commission',
  independence: 'Independence',
  commute: 'Commute',
  safety: 'Safety',
  internal_communication: 'Internal Communication',
  office_politics: 'Office Politics',
  harassment: 'Harassment',
  honesty: 'Honesty',
  ethics: 'Ethics',
  commitment: 'Commitment',
  compliance: 'Compliance',
  integrity: 'Integrity',
};

export interface CompanySentiment {
  companyId: number;
  shortName: string;
  scores: SentimentScores;
}

interface UseSentimentComparisonResult {
  sentiments: CompanySentiment[];
  isLoading: boolean;
  error: string | null;
}

/** Maximum entities per API request */
const BATCH_SIZE = 10;

/** Split an array into chunks of a given size */
const chunk = <T>(arr: T[], size: number): T[][] => {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
};

/**
 * Extract and normalize a sentiment score from the API response.
 * The API returns values on a 0–100 scale; we normalize to 0–1
 * so downstream consumers can display as ratings consistently
 * with the rest of the dashboard.
 */
const extractScore = (
  dimension:
    | { value?: number | null }
    | ReadonlyArray<{ value?: number | null } | null>
    | null
    | undefined
): number | null => {
  if (!dimension) return null;

  let raw: number | null | undefined;
  if (Array.isArray(dimension)) {
    const last = dimension[dimension.length - 1];
    raw = last?.value;
  } else {
    raw = (dimension as { value?: number | null }).value;
  }

  if (raw === null || raw === undefined) return null;
  return raw / 100;
};

/**
 * Parse a single sentiment API response entry into a CompanySentiment.
 */
export const parseSentimentEntry = (entry: {
  metadata?: { id?: number | string | null; shortName?: string | null } | null;
  result?: { scores?: Record<string, unknown> | null } | null;
}): CompanySentiment => {
  const scores = entry.result?.scores;
  const id = entry.metadata?.id;

  const extractedScores: Record<string, number | null> = {};
  for (const key of SENTIMENT_KEYS) {
    const dimension = scores?.[key];
    extractedScores[key] = extractScore(
      dimension as
        | { value?: number | null }
        | ReadonlyArray<{ value?: number | null } | null>
        | null
        | undefined
    );
  }

  return {
    companyId:
      typeof id === 'string' ? parseInt(id, 10) : ((id as number) ?? 0),
    shortName: entry.metadata?.shortName ?? '',
    scores: extractedScores as unknown as SentimentScores,
  };
};

export const useSentimentComparison = (
  competitors: Competitor[],
  resolvedIds: ResolvedProfileIds,
  additionalCompanyIds?: number[]
): UseSentimentComparisonResult => {
  const gqlClient = useClient();
  const [sentiments, setSentiments] = useState<CompanySentiment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allCompanyIds = useMemo(() => {
    const ids = competitors.map((c) => c.id);
    if (resolvedIds.companyRcid && !ids.includes(resolvedIds.companyRcid)) {
      ids.unshift(resolvedIds.companyRcid);
    }
    for (const id of additionalCompanyIds ?? []) {
      if (!ids.includes(id)) {
        ids.push(id);
      }
    }
    return ids;
  }, [competitors, resolvedIds.companyRcid, additionalCompanyIds]);

  // Stable key for the effect dependency
  const idsKey = allCompanyIds.join(',');

  // Track whether the component is still mounted
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchBatched = useCallback(async () => {
    if (allCompanyIds.length === 0) {
      setSentiments([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const batches = chunk(allCompanyIds, BATCH_SIZE);

      const results = await Promise.all(
        batches.map((batch) =>
          gqlClient
            .query(SENTIMENT_GET_MAP_DATA, {
              filters: { company: batch },
              dim1: Dimension1.Company,
            })
            .toPromise()
        )
      );

      if (!mountedRef.current) return;

      const merged: CompanySentiment[] = [];
      for (const result of results) {
        if (result.error) {
          setError('Failed to load sentiment data');
          continue;
        }
        const raw = result.data?.sentiment;
        if (!raw || !Array.isArray(raw)) continue;

        for (const entry of raw) {
          if (entry) {
            merged.push(parseSentimentEntry(entry));
          }
        }
      }

      setSentiments(merged);
    } catch {
      if (mountedRef.current) {
        setError('Failed to load sentiment data');
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [allCompanyIds, gqlClient]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    fetchBatched();
  }, [idsKey]);

  return { sentiments, isLoading, error };
};
